import React, { useState, useMemo } from 'react';
import ModalOverlay from '../../shared/ModalOverlay';
import { Record } from '../../types';
import { resolveLdgInductionDate } from '../../utils/dates';

interface GanttPickerProps {
  active: boolean;
  onClose: () => void;
  records: Record[];
  onDisplay: (start: string, end: string) => void;
}

interface GanttChartProps {
  active: boolean;
  onClose: () => void;
  records: Record[];
  startDate: string;
  endDate: string;
}

const statusBarClass: { [key: string]: string } = {
  Complete: 'status-complete',
  'In Work': 'status-in-work',
  'In Transit': 'status-in-transit',
  'In Planning': 'status-in-planning',
  Scheduled: 'status-scheduled',
  'On Hold': 'status-on-hold',
};

export const GanttPickerModal: React.FC<GanttPickerProps> = ({ active, onClose, records, onDisplay }) => {
  // Bars span LDG Induction Date → LDG Delivery Date, so the default range is
  // bounded by the earliest LDG induction and the latest delivery across records.
  const defaultStart = records.reduce((min, r) => {
    const ldgInduction = resolveLdgInductionDate(r.ldg_induction_date, r.induction_date);
    const d = r.delivery_date && r.delivery_date < (ldgInduction || '9999') ? r.delivery_date : ldgInduction;
    return d && d < min ? d : min;
  }, '9999-12-31');
  const defaultEnd = records.reduce((max, r) => {
    const ldgInduction = resolveLdgInductionDate(r.ldg_induction_date, r.induction_date);
    const d = ldgInduction && ldgInduction > (r.delivery_date || '0000') ? ldgInduction : r.delivery_date;
    return d && d > max ? d : max;
  }, '0000-01-01');

  const [start, setStart] = useState(defaultStart !== '9999-12-31' ? defaultStart : '');
  const [end, setEnd] = useState(defaultEnd !== '0000-01-01' ? defaultEnd : '');

  return (
    <ModalOverlay active={active} onClose={onClose}>
      <div className="modal modal-sm">
        <div className="modal-header">
          <h2>Create Gantt</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div className="gantt-date-row">
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onDisplay(start, end)}>Display</button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export const GanttChartModal: React.FC<GanttChartProps> = ({ active, onClose, records, startDate, endDate }) => {
  const { days, filteredRecords } = useMemo(() => {
    const s = new Date(startDate + 'T00:00:00');
    const e = new Date(endDate + 'T00:00:00');
    const dayList: Date[] = [];
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      dayList.push(new Date(d));
    }

    const filtered = records.filter((r) => {
      const ldgInduction = resolveLdgInductionDate(r.ldg_induction_date, r.induction_date);
      if (!ldgInduction || !r.delivery_date) return false;
      const recordStart = r.delivery_date < ldgInduction ? r.delivery_date : ldgInduction;
      const recordEnd = r.delivery_date < ldgInduction ? ldgInduction : r.delivery_date;
      return recordEnd >= startDate && recordStart <= endDate;
    });

    return { days: dayList, filteredRecords: filtered };
  }, [records, startDate, endDate]);

  const dayWidth = Math.max(24, Math.min(40, 800 / days.length));
  const totalDays = days.length;

  const getDayOffset = (dateStr: string): number => {
    const d = new Date(dateStr + 'T00:00:00');
    const s = new Date(startDate + 'T00:00:00');
    return Math.floor((d.getTime() - s.getTime()) / 86400000);
  };

  return (
    <ModalOverlay active={active} onClose={onClose}>
      <div className="modal gantt-chart-modal">
        <div className="modal-header">
          <h2>Gantt Chart: {startDate} to {endDate}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div style={{ padding: '1rem 1.5rem', overflowX: 'auto' }}>
          {!filteredRecords.length ? (
            <p className="gantt-empty">No records in this date range.</p>
          ) : (
            <div className="gantt-container">
              <div className="gantt-header">
                <div className="gantt-label-col">Tail #</div>
                <div className="gantt-timeline-header">
                  {days.map((d, i) => {
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    const isMonthStart = d.getDate() === 1;
                    return (
                      <div
                        key={i}
                        className={`gantt-day-header ${isWeekend ? 'weekend' : ''} ${isMonthStart ? 'month-start' : ''}`}
                        style={{ width: dayWidth }}
                      >
                        {isMonthStart ? d.toLocaleString('en-US', { month: 'short' }) : i === 0 ? `${d.getMonth() + 1}/${d.getDate()}` : d.getDate()}
                      </div>
                    );
                  })}
                </div>
              </div>
              {filteredRecords.map((r) => {
                const ldgInduction = resolveLdgInductionDate(r.ldg_induction_date, r.induction_date);
                const startOffset = getDayOffset(ldgInduction);
                const endOffset = getDayOffset(r.delivery_date);
                const barStart = Math.max(0, Math.min(startOffset, endOffset));
                const barEnd = Math.min(totalDays - 1, Math.max(startOffset, endOffset));
                const barWidth = Math.max(1, barEnd - barStart + 1) * dayWidth;
                const barLeft = barStart * dayWidth;
                const barClass = statusBarClass[r.status] || 'status-default';

                return (
                  <div key={r.id} className="gantt-row">
                    <div className="gantt-row-label">{r.carrier} {r.tail}</div>
                    <div className="gantt-row-timeline" style={{ width: totalDays * dayWidth }}>
                      {days.map((d, i) => (
                        <div
                          key={i}
                          className={`gantt-cell ${d.getDay() === 0 || d.getDay() === 6 ? 'weekend' : ''} ${d.getDate() === 1 ? 'month-start' : ''}`}
                          style={{ width: dayWidth }}
                        />
                      ))}
                      <div
                        className={`gantt-bar ${barClass}`}
                        style={{ left: barLeft, width: barWidth }}
                        title={`${r.tail}: ${ldgInduction} → ${r.delivery_date} (${r.status})`}
                      >
                        {barWidth > 60 ? r.tail : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
};
